import {
  Flex,
  Card,
  Text,
  Button,
  Link,
  InfoIcon,
  Notice,
  Tooltip,
  ExternalLinkIcon,
  Separator,
} from '@buffer-mono/popcorn'
import { addDays } from 'date-fns'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { capitalize } from '~publish/helpers/capitalize'
import { useDismissObject } from '~publish/hooks/useDismissObject'
import { hasDismissedObject } from '~publish/legacy/utils/dismissedObjects/hasDismissedObject'

import { getSettingsUrl } from '../../shared/utils/getSettingsUrl'
import { SettingsRow } from '../../shared/components/SettingsRow'
import { useGetPortalSession } from '../hooks/useGetPortalSession'

import styles from './BillingInformation.module.css'

import type { JSX } from 'react'

export const BillingInformation_WebBillingDetails = graphql(/* GraphQL */ `
  fragment BillingInformation_WebBillingDetails on WebBillingDetails {
    address {
      city
      country
      line1
      line2
      state
      postalCode
    }
    taxDetails {
      taxExempt
      hasUnrecognizedTaxLocation
      taxIds {
        value
        verification {
          status
        }
      }
    }
  }
`)

const MISSING_ADDRESS_NOTICE_DISMISSAL_ID =
  'billing-notice-missing-billing-address'
const MISSING_ADDRESS_NOTICE_DISMISSAL_EXPIRY_DAYS = 14

const isValidTaxIdStatus = (status: string | null | undefined): boolean => {
  return Boolean(
    status && ['verified', 'unverified', 'pending'].includes(status),
  )
}

const DEFAULT_TAX_MESSAGING =
  'Your business status helps us determine if we need to charge you VAT (Value Added Tax) or the applicable sales tax in your region.'

type WebBilling = ResultOf<typeof BillingInformation_WebBillingDetails>
type TaxId = WebBilling['taxDetails']['taxIds'][number]

const TaxIdStatus = ({ taxIds }: { taxIds: TaxId[] }): JSX.Element => {
  return (
    <Flex direction="column" gap="xs">
      {taxIds.map((taxId) => {
        const taxIdStatus = taxId.verification.status
        const shouldRenderStatus = isValidTaxIdStatus(taxIdStatus)
        return (
          <Text key={taxId.value} color="subtle">
            Tax ID: {taxId.value}{' '}
            {shouldRenderStatus && (
              <Text color={taxIdStatus === 'verified' ? 'success' : 'warning'}>
                {capitalize(taxIdStatus)}
              </Text>
            )}
          </Text>
        )
      })}
    </Flex>
  )
}

const BusinessStatusTitle = (): JSX.Element => {
  return (
    <Text
      weight="medium"
      align="center"
      iconPosition="end"
      icon={
        <Tooltip content={DEFAULT_TAX_MESSAGING}>
          <InfoIcon size="small" color="subtle" />
        </Tooltip>
      }
    >
      Business Status
    </Text>
  )
}

const TaxStatusDetails = ({
  taxIds,
  taxExempt,
  portalSessionLoading,
  getCustomerPortalSession,
}: {
  taxIds: TaxId[]
  taxExempt?: string | null
  portalSessionLoading: boolean
  getCustomerPortalSession: (args: { returnURL: string; cta: string }) => void
}): JSX.Element => {
  const hasTaxIds = taxIds.length > 0

  if (hasTaxIds && taxExempt !== 'exempt') {
    return (
      <Flex direction="column" gap="xs">
        <BusinessStatusTitle />
        <Flex as="span" className={styles.taxStatusContainer}>
          <Text color="subtle">Using Buffer for Business</Text>
          <span className={styles.taxStatusSeparator}>&bull;</span>
          <TaxIdStatus taxIds={taxIds} />
        </Flex>

        <Text size="sm" color="subtle">
          No further action is required if you are a business without a VAT
          number (UK and EU) or a sales tax ID (US). If you have any questions,
          please contact us at{' '}
          <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
        </Text>
      </Flex>
    )
  }

  if (taxExempt === 'exempt') {
    return (
      <Flex direction="column" gap="xs">
        <BusinessStatusTitle />
        <Text color="subtle">You are tax-exempt</Text>
        {hasTaxIds && <TaxIdStatus taxIds={taxIds} />}
        <Text size="sm" color="subtle">
          To update your tax information, please contact us at{' '}
          <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
        </Text>
      </Flex>
    )
  }

  return (
    <>
      <Flex direction="column" gap="2xs">
        <BusinessStatusTitle />
        <Text color="subtle">
          Are you a business?{' '}
          <Link
            as="button"
            type="button"
            onClick={(): void => {
              getCustomerPortalSession({
                returnURL: getSettingsUrl(),
                cta: 'billing-billingInformation-addYourAddress-1',
              })
            }}
            disabled={portalSessionLoading}
          >
            Add your tax ID
          </Link>
        </Text>
      </Flex>
      <Text size="sm" color="subtle">
        If you are a business without a VAT number, no further action is
        required. If you have any questions please contact us at{' '}
        <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
      </Text>
    </>
  )
}

export const BillingInformation = ({
  webBilling: webBillingRef,
}: {
  webBilling: FragmentOf<typeof BillingInformation_WebBillingDetails>
}): JSX.Element | null => {
  const { account } = useAccount()
  const billing = readFragment(
    BillingInformation_WebBillingDetails,
    webBillingRef,
  )
  const { dismissObject } = useDismissObject()
  const { getCustomerPortalSession, loading: portalSessionLoading } =
    useGetPortalSession()

  const { address, taxDetails } = billing
  const { taxExempt, taxIds, hasUnrecognizedTaxLocation } = taxDetails

  const showMissingAddressNotice =
    hasUnrecognizedTaxLocation &&
    taxExempt !== 'exempt' &&
    !hasDismissedObject({
      account,
      objectId: MISSING_ADDRESS_NOTICE_DISMISSAL_ID,
    })

  const hasAddress = Boolean(
    address.line1 ||
    address.line2 ||
    address.city ||
    address.state ||
    address.postalCode ||
    address.country,
  )

  return (
    <>
      <Separator />
      {showMissingAddressNotice && (
        <Notice
          variant="error"
          onDismiss={(): void => {
            dismissObject({
              id: MISSING_ADDRESS_NOTICE_DISMISSAL_ID,
              expiresAt: addDays(
                new Date(),
                MISSING_ADDRESS_NOTICE_DISMISSAL_EXPIRY_DAYS,
              ).toISOString(),
            })
          }}
        >
          <Text>
            You do not have a billing address on file. Please add your info to
            ensure your subscription remains in good standing.
          </Text>
        </Notice>
      )}
      <SettingsRow id="billing-information">
        <SettingsRow.Header>
          <SettingsRow.Title>Additional Information</SettingsRow.Title>
        </SettingsRow.Header>
        <SettingsRow.Control>
          <Button
            variant="secondary"
            onClick={(): void => {
              getCustomerPortalSession({
                returnURL: getSettingsUrl(),
                cta: 'billing-billingInformation-updateInformation-1',
              })
            }}
            disabled={portalSessionLoading}
          >
            Update Information
            <ExternalLinkIcon />
          </Button>
        </SettingsRow.Control>
      </SettingsRow>
      <Card style={{ width: '100%' }}>
        <Flex direction="column" gap="md">
          {hasAddress ? (
            <Flex direction="column" gap="2xs">
              <Text weight="medium">Address</Text>
              <Text color="subtle">
                {[
                  address.line1,
                  address.line2,
                  address.city,
                  address.state,
                  address.postalCode,
                  address.country,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            </Flex>
          ) : (
            <Flex direction="column" gap="2xs">
              <Text>You haven&apos;t added your address yet.</Text>
              <Button
                type="button"
                variant="tertiary"
                size="small"
                onClick={(): void => {
                  getCustomerPortalSession({
                    returnURL: getSettingsUrl(),
                    cta: 'billing-billingInformation-addYourAddress-1',
                  })
                }}
                disabled={portalSessionLoading}
              >
                Add Your Address
                <ExternalLinkIcon />
              </Button>
            </Flex>
          )}
          <TaxStatusDetails
            taxIds={taxIds}
            taxExempt={taxExempt}
            portalSessionLoading={portalSessionLoading}
            getCustomerPortalSession={getCustomerPortalSession}
          />
        </Flex>
      </Card>
    </>
  )
}

export default BillingInformation
